import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import CarePlanSection from "@/app/[tenant]/(console)/patient/(patient-page)/[id]/tabs/overview-tab/care-plan-section";
import DemographicsSection from "@/app/[tenant]/(console)/patient/(patient-page)/[id]/tabs/overview-tab/demographics-section";
import DevicesSection from "@/app/[tenant]/(console)/patient/(patient-page)/[id]/tabs/overview-tab/devices-section";
import InsuranceSection from "@/app/[tenant]/(console)/patient/(patient-page)/[id]/tabs/overview-tab/insurance-section";
import MeasurementsOverview from "@/app/[tenant]/(console)/patient/(patient-page)/[id]/tabs/overview-tab/measurements-overview";
import MedicationsSection from "@/app/[tenant]/(console)/patient/(patient-page)/[id]/tabs/overview-tab/medications-section";
import MemosSection from "@/app/[tenant]/(console)/patient/(patient-page)/[id]/tabs/overview-tab/memos-section";
import PatientHealthSection from "@/app/[tenant]/(console)/patient/(patient-page)/[id]/tabs/overview-tab/patient-health-section";
import PharmaciesSection from "@/app/[tenant]/(console)/patient/(patient-page)/[id]/tabs/overview-tab/pharmacies-section";
import PurchasesSection from "@/app/[tenant]/(console)/patient/(patient-page)/[id]/tabs/overview-tab/purchases-section";
import AISummarySection from "@/components/patient/ai-summary-section";
import SupplementsSection from "@/components/patient/supplements/supplements-section";
import { useModule } from "@/hooks/use-module";
import type { ChargeTableItem } from "@/types/charge";
import type { FormTable, PatientSubmissionsTable } from "@/types/forms/form";
import { OverviewSection, OverviewSectionPreference } from "@/types/overview-layout";
import type { PatientChart } from "@/types/patient";
import type { QuickNoteSummary } from "@/types/quick-note";
import type { Module } from "@/types/tenant";
export interface PatientOverviewProps {
  patient: PatientChart;
  shouldUseTwoColumns: boolean;
  charges?: ChargeTableItem[];
  forms?: FormTable[];
  formSubmissions?: PatientSubmissionsTable;
  quickNotes?: QuickNoteSummary[];
}
export interface OverviewSectionMeta {
  label: string;
  render: (props: PatientOverviewProps) => ReactNode;
  /**
   * The module a clinic must have for this section to exist at all.
   *
   * The section component's own gate is not enough in the layout editor.
   * `DevicesSection` returns null without WEARABLES, but `EditableSectionFrame`
   * wraps it from the OUTSIDE and draws the border, drag handle and hide button
   * unconditionally — so a clinic without the module saw an empty "Devices"
   * frame it could drag and hide, and the same label sitting in the
   * hidden-sections rail. Declared here so both surfaces filter on one fact
   * instead of each re-deriving it, and so a future gated section cannot
   * reintroduce the hole by forgetting one of them.
   */
  requiresModule?: Module;
}
export const OVERVIEW_SECTIONS: Record<OverviewSection, OverviewSectionMeta> = {
  [OverviewSection.AiSummary]: {
    label: "AI Summary",
    render: p => <AISummarySection patient={p.patient} charges={p.charges} forms={p.forms} formSubmissions={p.formSubmissions} quickNotes={p.quickNotes} />
  },
  [OverviewSection.Memos]: {
    label: "Memos",
    render: p => <MemosSection patient={p.patient} shouldUseTwoColumns={p.shouldUseTwoColumns} />
  },
  [OverviewSection.CarePlans]: {
    label: "Care Plans",
    render: p => <div className="w-full">
        <CarePlanSection patient={p.patient} />
      </div>
  },
  [OverviewSection.Demographics]: {
    label: "Demographics",
    render: p => <div id="demographics-section">
        <DemographicsSection patient={p.patient} shouldUseTwoColumns={p.shouldUseTwoColumns} />
      </div>
  },
  [OverviewSection.Insurance]: {
    label: "Insurance",
    render: p => <div id="insurance-section">
        <InsuranceSection patient={p.patient} shouldUseTwoColumns={p.shouldUseTwoColumns} />
      </div>
  },
  [OverviewSection.Measurements]: {
    label: "Measurements",
    render: p => <div id="measurements-section">
        <MeasurementsOverview patient={p.patient} />
      </div>
  },
  [OverviewSection.Devices]: {
    label: "Devices",
    requiresModule: "WEARABLES",
    render: p => <DevicesSection patient={p.patient} />
  },
  [OverviewSection.Medications]: {
    label: "Medications",
    render: p => <MedicationsSection patient={p.patient} />
  },
  [OverviewSection.Supplements]: {
    label: "Supplements",
    render: p => <SupplementsSection patientId={p.patient.id} id="supplements-section" persistKey="supplements" />
  },
  [OverviewSection.Pharmacies]: {
    label: "Pharmacies",
    render: p => <PharmaciesSection patient={p.patient} forms={p.forms} />
  },
  [OverviewSection.PatientHealth]: {
    label: "Patient Health",
    render: p => <PatientHealthSection patient={p.patient} shouldUseTwoColumns={p.shouldUseTwoColumns} />
  },
  [OverviewSection.Purchases]: {
    label: "Purchases",
    render: p => <div id="purchases-section">
        <PurchasesSection patient={p.patient} />
      </div>
  }
};

/**
 * Whether this clinic can show a given section.
 *
 * A hook rather than a predicate on the meta itself: availability is tenant
 * module state, and a hook cannot be called once per entry inside a filter.
 * Every gated module is read unconditionally here, so gating another section is
 * a line in this map rather than a change of shape at the call sites.
 */
export function useOverviewSectionAvailable() {
  const $ = _c(2);
  const wearablesEnabled = useModule("WEARABLES");
  let t0;
  if ($[0] !== wearablesEnabled) {
    const enabledByModule = {
      WEARABLES: wearablesEnabled
    };
    t0 = section => {
      const required = OVERVIEW_SECTIONS[section].requiresModule;
      return required === undefined ? true : enabledByModule[required] ?? false;
    };
    $[0] = wearablesEnabled;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export const DEFAULT_OVERVIEW_ORDER: OverviewSection[] = [OverviewSection.AiSummary, OverviewSection.Memos, OverviewSection.CarePlans, OverviewSection.Demographics, OverviewSection.Insurance, OverviewSection.Measurements, OverviewSection.Devices, OverviewSection.Medications, OverviewSection.Supplements, OverviewSection.Pharmacies, OverviewSection.PatientHealth, OverviewSection.Purchases];

/**
 * Merge stored prefs with defaults — every enum member is guaranteed in the
 * returned list. Sections present on the server keep their position/visible;
 * any not yet stored are appended (visible) after the highest stored position.
 */
export function resolveSections(stored: OverviewSectionPreference[] | undefined): OverviewSectionPreference[] {
  const bySection = new Map<OverviewSection, OverviewSectionPreference>();
  for (const pref of stored ?? []) {
    bySection.set(pref.section, pref);
  }
  let nextPosition = stored?.length ? Math.max(...stored.map(p => p.position)) + 1 : 0;
  const merged: OverviewSectionPreference[] = [];
  for (const section of DEFAULT_OVERVIEW_ORDER) {
    const existing = bySection.get(section);
    if (existing) {
      merged.push(existing);
    } else {
      merged.push({
        section,
        position: nextPosition,
        visible: true
      });
      nextPosition += 1;
    }
  }
  return merged;
}
export function defaultLayout(): OverviewSectionPreference[] {
  return DEFAULT_OVERVIEW_ORDER.map((section, position) => ({
    section,
    position,
    visible: true
  }));
}